import type { Evidence, SummaryViewStep, TerminalProgressInput } from '@areapif/areapif-shared'
import {
  deriveTerminalProgressId,
  InspectionStatusId,
  InspectionStepId,
  InspectionTerminalProgressId,
  SummaryViewStepStatus,
} from '@areapif/areapif-shared'

/**
 * Coarse lifecycle state shown in the backoffice inspections table.
 *
 * Operators reason about four states: not started yet, being worked on, done,
 * cancelled. Both inspection lifecycles land here — the PIF ladder through
 * {@link deriveInspectionState}, which merges its intermediate statuses into a
 * single IN_PROGRESS bucket, and the terminal one through
 * {@link deriveTerminalInspectionState}, which takes the shared domain's answer
 * rather than deriving a second one.
 */
export enum InspectionState {
  PLANNED = 'PLANNED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
}

export const INSPECTION_STATES = [
  InspectionState.PLANNED,
  InspectionState.IN_PROGRESS,
  InspectionState.FINISHED,
  InspectionState.CANCELLED,
] as const

/**
 * How each status of the PIF ladder collapses onto the coarse state.
 *
 * `LLEGADA AL FINGER` is deliberately absent: it is the boundary status, set
 * both when the container arrives (nothing done yet) and while the inspection
 * is being carried out, so evidence — not the status — separates the two.
 *
 * The terminal-vocabulary statuses are absent too, and that is the point. The
 * database keeps both lifecycles in one `status` enum, split by a CHECK
 * constraint keyed on `inspections.type`; a terminal status arriving here means
 * a row went down the PIF branch it does not belong to, and saying so beats
 * inventing a state for it.
 */
const PIF_STATE_BY_STATUS: Partial<Record<InspectionStatusId, InspectionState>> = {
  CREADO: InspectionState.PLANNED,
  'EN EL PARKING': InspectionState.PLANNED,
  'ASIGNADO A FINGER': InspectionState.PLANNED,
  'DESCARGA INICIADA': InspectionState.IN_PROGRESS,
  'DESCARGA FINALIZADA': InspectionState.IN_PROGRESS,
  'CARGA INICIADA': InspectionState.IN_PROGRESS,
  'CARGA FINALIZADA': InspectionState.IN_PROGRESS,
  PRECINTADO: InspectionState.IN_PROGRESS,
  'PARKING FINAL': InspectionState.IN_PROGRESS,
  FINALIZADO: InspectionState.FINISHED,
  ANULADO: InspectionState.CANCELLED,
}

/**
 * Derive the coarse state of a *PIF* inspection from its position on the ladder.
 *
 * Terminal inspections do not resolve through here — they have no finger and no
 * unload/load phases. {@link deriveTerminalInspectionState} handles those.
 *
 * @throws if `statusId` carries no PIF lifecycle meaning. There is no
 * catch-all: a status this function has not heard of is drift between the app
 * and the database, and the caller surfaces it as a skipped row rather than
 * letting it wear a state it was never assigned.
 */
export const deriveInspectionState = (statusId: InspectionStatusId, evidenceCount: number): InspectionState => {
  if (statusId === 'LLEGADA AL FINGER') {
    return evidenceCount === 0 ? InspectionState.PLANNED : InspectionState.IN_PROGRESS
  }

  const state = PIF_STATE_BY_STATUS[statusId]

  if (state === undefined) {
    throw new Error(`'${statusId}' is not a PIF status: it carries no PIF lifecycle meaning and cannot be collapsed into a state`)
  }

  return state
}

/**
 * The statuses that render without a qualifier on the state badge.
 *
 * Everything else earns one, because the coarse state alone does not say where
 * on the ladder the row is: four statuses collapse onto PLANNED and seven onto
 * IN_PROGRESS. These four are the exceptions, for two different reasons.
 *
 * `PLANTEADO` and `EN_CURSO` are the terminal vocabulary — a PIF row never
 * holds them, and a terminal row is not qualified at all.
 *
 * `FINALIZADO` and `ANULADO` are the two shared end states, and each is the
 * only status that reaches its state. Qualifying them would render
 * `Finalizada (Finalizado)`: a parenthetical that repeats the word before it.
 */
const UNQUALIFIED_STATUSES = {
  PLANTEADO: true,
  EN_CURSO: true,
  FINALIZADO: true,
  ANULADO: true,
} as const satisfies Partial<Record<InspectionStatusId, true>>

/**
 * A status the badge appends to the state label, in parentheses.
 *
 * Derived by subtraction rather than enumerated, so a status added to the
 * database enum arrives here needing a label, and the missing translation is a
 * compile error rather than a blank parenthetical on screen.
 */
export type QualifiedStatusId = Exclude<InspectionStatusId, keyof typeof UNQUALIFIED_STATUSES>

export const isQualifiedStatusId = (statusId: InspectionStatusId): statusId is QualifiedStatusId => !(statusId in UNQUALIFIED_STATUSES)

/**
 * The shared terminal lifecycle, in the vocabulary this table renders.
 *
 * Exhaustive by type rather than by convention: a progress id added to the
 * shared package breaks this file at compile time instead of falling through.
 */
const STATE_BY_TERMINAL_PROGRESS: Record<InspectionTerminalProgressId, InspectionState> = {
  [InspectionTerminalProgressId.PLANNED]: InspectionState.PLANNED,
  [InspectionTerminalProgressId.IN_PROGRESS]: InspectionState.IN_PROGRESS,
  [InspectionTerminalProgressId.FINISHED]: InspectionState.FINISHED,
  [InspectionTerminalProgressId.CANCELLED]: InspectionState.CANCELLED,
}

/**
 * Derive the coarse state of a *terminal* inspection, from the shared domain.
 *
 * The shared derivation reads `status` only to answer "finished or cancelled?"
 * and takes the open states from SOSTRAT provenance and evidence. That shape is
 * what keeps this repo still when the backend moves terminal rows onto the
 * `PLANTEADO` / `EN_CURSO` spellings: only the open values change, and nothing
 * here reads them. Re-deriving the same states locally is what this replaces.
 */
export const deriveTerminalInspectionState = (input: TerminalProgressInput): InspectionState =>
  STATE_BY_TERMINAL_PROGRESS[deriveTerminalProgressId(input)]

export interface EvidenceStepView {
  stepId: InspectionStepId
  status: SummaryViewStepStatus
  count: number
  /**
   * The step's photos and classified seals, in the order the summary view
   * lists them: what the strip previews when the step is hovered. Always the
   * `count` items — both come off the same array — so the badge never promises
   * evidence the preview cannot show.
   */
  evidence: Evidence[]
}

/**
 * What a step the flow calls ACTIVE reads as, per state of the row it sits on.
 *
 * The shared summary view anchors every phase to the raw status that opens it —
 * phase A to `LLEGADA AL FINGER`, phase OTHER to `FINALIZADO` — and reports that
 * phase ACTIVE whenever the row holds its anchor. Two of those anchors are not
 * statuses of an inspection being worked on: the first is where a container
 * merely arrives, the second is where the inspection ends. The strip renders
 * beside the state badge and must answer to the same state it does, so ACTIVE
 * belongs to IN_PROGRESS alone — ahead of that a step is still pending, and
 * past it a step without evidence is a gap, exactly like the ones beside it.
 */
const ACTIVE_STEP_STATUS_BY_STATE: Record<InspectionState, SummaryViewStepStatus> = {
  [InspectionState.PLANNED]: SummaryViewStepStatus.TODO,
  [InspectionState.IN_PROGRESS]: SummaryViewStepStatus.ACTIVE,
  [InspectionState.FINISHED]: SummaryViewStepStatus.DONE_MISSING,
  // Unreachable: the strip of a cancelled inspection is emptied before it is
  // built. Spelled out anyway so the record stays exhaustive over the enum, and
  // a fifth state cannot be added without deciding what it renders.
  [InspectionState.CANCELLED]: SummaryViewStepStatus.TODO,
}

/**
 * Per-step status, refining the phase status the shared library exposes.
 *
 * The shared `summaryView` reports status per *phase*; the table needs it per
 * *step* so a half-done phase reads correctly: the steps already covered show as
 * done and only the ones actually missing are flagged.
 *
 * `isBehindEvidence` says whether some later step of the flow already carries
 * evidence. The flow is linear, so an empty step behind one is a gap the
 * inspector left, whatever its phase claims: the shared view anchors phase
 * status to the raw SOSTRAT status and cannot see that the work has moved on.
 * Without this the strip renders a blue or grey circle to the left of a green
 * one, and the operator cannot tell a skipped step from the one being worked.
 */
const getStepStatus = (
  phaseStatus: SummaryViewStepStatus,
  hasEvidence: boolean,
  isBehindEvidence: boolean,
  state: InspectionState,
): SummaryViewStepStatus => {
  if (hasEvidence) {
    return SummaryViewStepStatus.DONE_COMPLETE
  }

  if (isBehindEvidence) {
    return SummaryViewStepStatus.DONE_MISSING
  }

  switch (phaseStatus) {
    case SummaryViewStepStatus.ACTIVE:
      return ACTIVE_STEP_STATUS_BY_STATE[state]
    case SummaryViewStepStatus.DONE_COMPLETE:
    case SummaryViewStepStatus.DONE_MISSING:
      return SummaryViewStepStatus.DONE_MISSING
    case SummaryViewStepStatus.TODO:
      return SummaryViewStepStatus.TODO
  }
}

/**
 * Flatten the shared summary view into the ordered strip of steps rendered in
 * the table — the visual representation of the evidence state machine.
 *
 * Takes the row's derived state as well as its summary view, because the flow
 * alone cannot say whether the inspection is under way: see
 * {@link ACTIVE_STEP_STATUS_BY_STATE}. Reads the strip as a whole rather than
 * step by step, because a step's status also depends on what comes after it:
 * see {@link getStepStatus}.
 */
export const buildEvidenceSteps = (summaryView: SummaryViewStep[], state: InspectionState): EvidenceStepView[] => {
  const steps = summaryView.flatMap(phase => phase.steps.map(step => ({ phaseStatus: phase.status, step })))
  const lastCoveredIndex = steps.reduce((last, { step }, index) => (step.evidence.length > 0 ? index : last), -1)

  return steps.map(({ phaseStatus, step }, index) => ({
    stepId: step.stepId,
    status: getStepStatus(phaseStatus, step.evidence.length > 0, index < lastCoveredIndex, state),
    count: step.evidence.length,
    evidence: step.evidence,
  }))
}

export interface EvidenceCounts {
  total: number
  photos: number
  seals: number
  /**
   * Seals uploaded but not yet classified. They count towards the totals — the
   * file is there — but carry no category, so they cannot be placed on a step of
   * the flow until processing finishes.
   */
  unclassifiedSeals: number
}
